import axios from "axios";
import { getAPIKeys, getURLPrefix } from "utils/getUrlPrefix";
import { getAuthTokens, setAuthTokens } from "../auth/tokens";

const URL_PREFIX = getURLPrefix();
const apiKeys = getAPIKeys();
const CLIENT_ID = apiKeys["API_AUTH_CLIENT_ID"];
const CLIENT_SECRET = apiKeys["API_AUTH_SECRET"];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function encodeAxiosParam(value: any) {
  let v = value;
  if (toString.call(value) === "[object Date]") {
    v = value.toISOString();
  } else if (typeof value === "object") {
    v = JSON.stringify(value);
  }
  return encodeURIComponent(v);
}

/**
 * Serializes params to a query string like axios pre-v1
 * @link https://github.com/axios/axios/issues/5630#issuecomment-1859776798
 */
function paramsSerializer(params: object) {
  return Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : true))
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return value.map((v) => `${key}[]=${encodeAxiosParam(v)}`).join("&");
      }
      return `${key}=${encodeAxiosParam(value)}`;
    })
    .join("&");
}

// Is the marker being refreshed?
let isRefreshing = false;
// Retry queue, each item will be a function to be executed
let requests: Array<(token: string) => void> = [];

export const configureAxios = () => {
  axios.interceptors.response.use(
    async (response) => {
      const code = response.status;
      const url = response.config.url;
      if (code === 401 && !url?.includes("/auth")) {
        const config = response.config;
        if (!isRefreshing) {
          isRefreshing = true;
          const { refresh_token = null } = getAuthTokens() ?? {};
          if (!refresh_token) {
            return response;
          }
          const userInfo = {
            grant_type: "refresh_token",
            client_id: CLIENT_ID,
            client_secret: CLIENT_SECRET,
            refresh_token,
          };

          return await axios
            .post(URL_PREFIX + "/auth/token", userInfo)
            .then(async (res) => {
              setAuthTokens(res.data);
              // token has been refreshed to retry requests from all queues
              requests.forEach((cb) => cb(res.data.access_token));
              requests = [];
              config.headers["Authorization"] = `Bearer ${res.data.access_token}`;
              return await axios(config);
            })
            .catch(() => {
              return response;
            })
            .finally(() => {
              isRefreshing = false;
            });
        } else {
          // token is being refreshed and a promise that resolve has not been executed is returned
          return await new Promise((resolve) => {
            // Put resolve in the queue, save it in a function form, and execute it directly after token refreshes
            requests.push((token: string) => {
              config.headers["Authorization"] = `Bearer ${token}`;
              resolve(axios(config));
            });
          });
        }
      }
      return response;
    },
    async (error: Error) => {
      return await Promise.reject(error);
    },
  );

  // Override the default axios paramsSerializer with pre-v1 behavior
  axios.defaults.paramsSerializer = paramsSerializer;
};
